import { ToWords } from 'to-words';

const toWords = new ToWords({ localeCode: 'en-US' });
import { formatToCurrency } from '@/lib/helpers';

/**
 * Generate the Pre Approval Checklist document
 *
 * @param {Object=} data - The data to be used to generate the document
 */
export const generatePreApprovalChecklist = (data = {}) => {
  const borderColor = '#9ca3af';
  const bigMargin = 20;

  const branchName = data.user?.branch?.branchName || '';
  const branchAddress = `${data?.user?.branch?.branchStreet} ${data?.user?.branch?.branchCity} ${data?.user?.branch?.branchProvince} ${data?.user?.branch?.branchCountry}`;
  // Get the main branch phone number
  const branchPhones = () => {
    if (!data?.user?.branch?.branchTelephone) return '';
    const mainBranch = data?.user?.branch?.branchTelephone.find(
      (t) => t.isMain == true
    );
    if (!mainBranch) return '';
    return mainBranch.number;
  };
  const accountHolders = () => {
    if (!data.loan?.loanDistributions) return '';
    return data.loan?.loanDistributions
      .map(
        (d) =>
          `${d.person?.firstName} ${d.person?.middleName} ${d.person?.lastName}`
      )
      .join(', ');
  };
  const tableLayout = {
    hLineColor: borderColor,
    vLineColor: borderColor,
    paddingBottom: () => 4,
    paddingTop: () => 4,
    paddingLeft: () => 8,
    paddingRight: () => 8
  };
  /**@type {import('pdfmake/interfaces').TDocumentDefinitions} */
  const dd = {
    footer: function (currentPage, pageCount) {
      return {
        text: `Page ${currentPage.toString()} of ${pageCount.toString()}`,
        alignment: 'center'
      };
    },
    info: {
      title: 'Approval Review', // name of the document being printed
      modDate: new Date(), // date the document was last modified
      creationDate: new Date(), // date the document was created
      keywords: 'apc, loan, finance', // keywords associated with the document
      author: data.company?.name, // company name
      subject: 'Approval Review', // name of the document being printed
      creator: data.user?.username, // name of the logged in user
      producer: data.company?.name // company name
    },
    content: [
      data.company?.companyImage && {
        image: 'companyImage',
        alignment: 'center',
        fit: [80, 80]
      },
      {
        marginTop: 20,
        text: data.company?.name,
        alignment: 'center',
        fontSize: 22,
        bold: true
      },
      {
        text: 'Approval Review',
        alignment: 'center',
        fontSize: 14,
        marginBottom: 10,
        bold: true
      },
      { text: branchName, alignment: 'center' },
      { alignment: 'center', text: branchAddress },
      { alignment: 'center', text: branchPhones() },
      { text: `Account No. ${data?.loan?.mainAccountNumber}`, marginTop: 30 },
      { text: accountHolders() },
      {
        marginTop: bigMargin,
        columns: [
          { text: 'Total Loan Amount:' },
          { text: formatToCurrency(3000000), alignment: 'right' }
        ]
      },

      {
        columns: [
          { text: 'Interest Rate (Annual):' },
          { text: '7.50%', alignment: 'right' }
        ]
      },
      {
        columns: [
          { text: 'Tenure:' },
          { text: '120 Month(s)', alignment: 'right' }
        ]
      },
      {
        columns: [
          { text: 'Payment (Monthly):' },
          { text: formatToCurrency(43750), alignment: 'right' }
        ]
      },
      {
        columns: [
          { text: 'Loan Product:' },
          { text: 'New Vehicle Loan', alignment: 'right' }
        ]
      },
      {
        alignment: 'left',
        layout: tableLayout,
        marginTop: bigMargin,
        marginBottom: bigMargin,
        table: {
          widths: [60, '*', 65, 60, '*'],
          headerRows: 1,
          body: [
            [
              { text: 'Item Code', style: 'tableHeader' },
              { text: 'Item', style: 'tableHeader' },
              { text: 'Item Type', style: 'tableHeader' },
              { text: 'Priority', style: 'tableHeader' },
              { text: 'Status', style: 'tableHeader' }
            ],
            [`C120`, `Loan Reviewed`, `Action`, `High`, `Not yet Completed`],
            [`C101`, `Verify ID`, `Action`, `High`, `Not yet Completed`]
          ]
        }
      },
      { text: 'Signature', marginTop: bigMargin, bold: true },
      {
        marginTop: 15,
        columns: [
          { text: '_______________________________', font: 'Roboto' },
          {
            text: '_______________________________',
            font: 'Roboto',
            alignment: 'right'
          }
        ]
      },
      {
        marginTop: 10,
        columns: [
          {
            text: `Prepared by: ${data.user?.username}`,
            style: 'supSignature',
            alignment: 'left'
          },
          { text: 'Date Signed', alignment: 'right', style: 'supSignature' }
        ]
      }
    ],
    images: {
      companyImage: data.company?.companyImage
    },
    styles: {
      tableHeader: {
        bold: true
      },
      supSignature: {
        fontSize: 18,
        sup: true,
        alignment: 'center'
      },
      tableHeading: {
        alignment: 'left',
        fontSize: 16,
        bold: true
      }
    },
    defaultStyle: {
      font: 'Lora',
      color: '#111827',
      fontSize: 11,
      lineHeight: 1.3
    }
  };

  return dd;
};
